import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import {
	PieChart,
	Pie,
	Cell,
	Tooltip,
	ResponsiveContainer,
} from "recharts";
import { getDeprecatedFirmwareSummary } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";
import { PieValueTooltip } from "../../components/ui/chart-tooltip";

function toNumber(value, fallback = 0) {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function formatDateTime(value) {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toISOString().slice(0, 19).replace("T", " ");
}

function formatDate(value) {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return String(value);
	return date.toISOString().slice(0, 10);
}

const PIE_SLICES = [
	{ key: "active", label: "Active Devices", color: "#ef4444" },
	{ key: "updated", label: "Updated", color: "#22c55e" },
	{ key: "pending", label: "Pending", color: "#f59e0b" },
	{ key: "mandatory_wait", label: "Mandatory Wait", color: "#f97316" },
	{ key: "failed", label: "Failed", color: "#b91c1c" },
	{ key: "received_it", label: "Received It", color: "#0d9488" },
	{ key: "skipped", label: "Skipped", color: "#64748b" },
];

function getSliceLabel(key) {
	return PIE_SLICES.find((slice) => slice.key === key)?.label ?? "All";
}

function getStatusMeta(device, rolloutCategory) {
	const category = String(rolloutCategory || "").toLowerCase();
	const rawStatus = String(device?.status || device?.update_status || "").toLowerCase();

	if (rawStatus === "deprecated") {
		return { label: "deprecated", className: "bg-red-100 text-red-700" };
	}

	if (category === "updated" || rawStatus === "updated") {
		return { label: "updated", className: "bg-green-100 text-green-700" };
	}
	if (category === "failed" || rawStatus === "failed") {
		return { label: "failed", className: "bg-red-100 text-red-700" };
	}
	if (category === "mandatory_wait") {
		return { label: "mandatory wait", className: "bg-orange-100 text-orange-700" };
	}
	if (category === "received_it") {
		return { label: "received it", className: "bg-teal-100 text-teal-700" };
	}
	if (category === "skipped") {
		return { label: "skipped", className: "bg-slate-100 text-slate-700" };
	}
	if (category === "active") {
		return { label: "deprecated", className: "bg-red-100 text-red-700" };
	}

	return { label: "deprecated", className: "bg-red-100 text-red-700" };
}

function normalizeDevices(record) {
	if (Array.isArray(record?.devices)) {
		return record.devices.map((device, index) => ({
			...device,
			device_id: device?.device_id || `${device?.serial_number || "device"}-${index}`,
			model: device?.model || device?.device_type || "",
			current_country: device?.current_country ?? device?.country ?? "",
			status: "deprecated",
			__rolloutCategory: "active",
		}));
	}

	if (record?.devices && !Array.isArray(record.devices)) {
		return Object.entries(record.devices).flatMap(([category, devices]) =>
			(devices ?? []).map((device, index) => ({
				...device,
				device_id: device?.device_id || `${category}-${device?.serial_number || index}`,
				model: device?.model || device?.device_type || "",
				current_country: device?.current_country ?? device?.country ?? "",
				status: "deprecated",
				__rolloutCategory: category || "active",
			}))
		);
	}

	const deviceList = record?.devices_list ?? record?.device_list ?? record?.device_details ?? record?.active_devices_list;
	if (Array.isArray(deviceList)) {
		return deviceList.map((device, index) => ({
			...device,
			device_id: device?.device_id || `${device?.serial_number || "device"}-${index}`,
			model: device?.model || device?.device_type || "",
			current_country: device?.current_country ?? device?.country ?? "",
			status: "deprecated",
			__rolloutCategory: "active",
		}));
	}

	return [];
}

function buildPieData(record) {
	const data = PIE_SLICES.map((slice) => ({
		...slice,
		value: toNumber(record?.[slice.key], 0),
	})).filter((slice) => slice.value > 0);

	if (data.length > 0) return data;

	const fallbackActive = toNumber(record?.active_devices, 0);
	return fallbackActive > 0 ? [{ key: "active", label: "Active Devices", color: "#ef4444", value: fallbackActive }] : [];
}

export default function DeprecatedFirmwareBreakdownPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { firmwareKey } = useParams();
	const [loading, setLoading] = useState(true);
	const [deprecatedRecord, setDeprecatedRecord] = useState(null);
	const [selectedSlice, setSelectedSlice] = useState(null);

	const requestedFirmwareId = location.state?.firmwareId || "";
	const requestedVersion = location.state?.version || decodeURIComponent(firmwareKey || "");

	useEffect(() => {
		let mounted = true;

		const fetchData = async () => {
			setLoading(true);
			try {
				const data = await getDeprecatedFirmwareSummary();
				if (!mounted) return;

				const records = data?.deprecated_firmware ?? [];
				const match = records.find((item) => {
					const firmwareId = String(item?.firmware_id || "");
					const version = String(item?.version || "");
					return (
						(requestedFirmwareId && firmwareId === requestedFirmwareId) ||
						(requestedVersion && version === requestedVersion) ||
						String(item?.firmware_id || item?.version || "") === decodeURIComponent(firmwareKey || "")
					);
				});

				setDeprecatedRecord(match ?? null);
			} catch (error) {
				if (!mounted) return;
				console.error("Failed to fetch deprecated firmware breakdown", error);
				setDeprecatedRecord(null);
			} finally {
				if (mounted) setLoading(false);
			}
		};

		fetchData();
		return () => {
			mounted = false;
		};
	}, [firmwareKey, requestedFirmwareId, requestedVersion]);

	const pieData = useMemo(() => buildPieData(deprecatedRecord), [deprecatedRecord]);
	const allDevices = useMemo(() => normalizeDevices(deprecatedRecord), [deprecatedRecord]);
	const tableDevices = useMemo(() => {
		if (!selectedSlice) return allDevices;
		return allDevices.filter((device) => device.__rolloutCategory === selectedSlice);
	}, [allDevices, selectedSlice]);
	const totalDevices = pieData.reduce((sum, item) => sum + item.value, 0);


	if (loading) return <PageLoader />;

	if (!deprecatedRecord) {
		return (
			<div className="space-y-5">
				<div>
					<button
						onClick={() => navigate("/rollouts")}
						className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors mb-2"
					>
						<ArrowLeft className="h-4 w-4" />
						Back to Rollout Summary
					</button>
					<h1 className="text-2xl font-bold text-slate-900">Deprecated Firmware Breakdown</h1>
				</div>
				<div className="bg-white border border-slate-200 rounded-2xl px-6 py-8 text-sm text-slate-500 shadow-sm">
					No details found for this deprecated firmware version.
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-5">
			<div className="flex items-start justify-between gap-3">
				<div>
					<button
						onClick={() => navigate("/rollouts")}
						className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 border border-slate-200 rounded-lg px-3 py-2 bg-white transition-colors mb-2"
					>
						<ArrowLeft className="h-4 w-4" />
						Back to Rollout Summary
					</button>
					<h1 className="text-2xl font-bold text-slate-900">Deprecated Firmware Breakdown</h1>
					<p className="text-sm text-slate-500 mt-1">Version {deprecatedRecord?.version || requestedVersion || "--"}</p>
				</div>
				{selectedSlice && (
					<button
						onClick={() => setSelectedSlice(null)}
						className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:text-slate-800"
					>
						<X className="h-3 w-3" /> Clear filter
					</button>
				)}
			</div>

			<div className="bg-white border border-slate-200 rounded-2xl px-6 py-6 md:px-7 md:py-7 shadow-sm">
				<div className="grid gap-3 md:grid-cols-3">
					<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Active Devices</p>
						<p className="mt-1 text-2xl font-bold text-red-500">{toNumber(deprecatedRecord?.active_devices, totalDevices)}</p>
					</div>
					<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Deprecated On</p>
						<p className="mt-1 text-sm font-semibold text-slate-800">{formatDate(deprecatedRecord?.deprecated_at) || "—"}</p>
					</div>
					<div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
						<p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Reason</p>
						<p className="mt-1 text-sm font-semibold text-slate-800">{deprecatedRecord?.deprecation_reason || "—"}</p>
					</div>
				</div>
			</div>

			<div className="bg-white border border-slate-200 rounded-2xl px-6 py-6 md:px-7 md:py-7 space-y-6 shadow-sm">
				<div>
					<h2 className="text-base font-semibold text-slate-800 mb-4">Device Breakdown</h2>
					{pieData.length === 0 ? (
						<p className="text-sm text-slate-400">No device data available.</p>
					) : (
						<div className="grid gap-6 lg:grid-cols-[360px,1fr] items-center">
							<div className="h-[300px] rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
								<ResponsiveContainer width="100%" height="100%">
									<PieChart>
										<Pie
											data={pieData}
											cx="50%"
											cy="50%"
											innerRadius={72}
											outerRadius={108}
											paddingAngle={2}
											dataKey="value"
											onClick={(entry) => {
												setSelectedSlice((prev) => (prev === entry.key ? null : entry.key));
											}}
											cursor="pointer"
										>
											{pieData.map((entry) => (
												<Cell
													key={entry.key}
													fill={entry.color}
													opacity={selectedSlice && selectedSlice !== entry.key ? 0.35 : 1}
													stroke={selectedSlice === entry.key ? "#0f172a" : "#ffffff"}
													strokeWidth={selectedSlice === entry.key ? 3 : 2}
												/>
											))}
										</Pie>
										<Tooltip content={<PieValueTooltip />} />
										<text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize="28" fontWeight="700">
											{totalDevices}
										</text>
										<text x="50%" y="58%" textAnchor="middle" dominantBaseline="middle" fill="#64748b" fontSize="12" fontWeight="500">
											Total Devices
										</text>
									</PieChart>
								</ResponsiveContainer>
							</div>

							<div className="grid gap-2.5 sm:grid-cols-2">
								{pieData.map((entry) => {
									const percent = totalDevices > 0 ? ((entry.value / totalDevices) * 100).toFixed(1) : "0.0";
									const isActive = selectedSlice === entry.key;
									return (
										<button
											key={entry.key}
											onClick={() => setSelectedSlice((prev) => (prev === entry.key ? null : entry.key))}
											className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
												isActive
													? "border-slate-400 bg-slate-50 shadow-sm"
													: "border-slate-200 bg-white hover:border-slate-300"
											}`}
										>
											<div className="flex items-center justify-between gap-3">
												<div className="flex items-center gap-2 min-w-0">
													<span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
													<p className="text-xs font-semibold text-slate-700 truncate">{entry.label}</p>
												</div>
												<p className="text-[11px] text-slate-500">{percent}%</p>
											</div>
											<p className="text-lg font-bold text-slate-900 mt-1">{entry.value}</p>
										</button>
									);
								})}
							</div>
						</div>
					)}
				</div>

				<div className="flex flex-wrap gap-2 pt-1">
					{pieData.map((entry) => (
						<button
							key={entry.key}
							onClick={() => setSelectedSlice((prev) => (prev === entry.key ? null : entry.key))}
							className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
								selectedSlice === entry.key
									? "bg-red-50 border-red-300 text-red-700"
									: "bg-white border-slate-300 text-slate-600"
							}`}
						>
							{entry.label} ({entry.value})
						</button>
					))}
				</div>

				<div>
					<h3 className="text-sm font-semibold text-slate-700 mb-3">
						{selectedSlice
							? `${getSliceLabel(selectedSlice)} (${tableDevices.length})`
							: `All Devices (${tableDevices.length})`}
					</h3>
					{tableDevices.length === 0 ? (
						<p className="text-sm text-slate-400">No device-level details returned for this firmware.</p>
					) : (
						<div className="overflow-x-auto rounded-lg border border-slate-200">
							<table className="min-w-full text-xs">
								<thead className="bg-slate-50">
									<tr>
										{[
											"Serial Number",
											"Model",
											"Current Country",
											"Current FW",
											"Base FW",
											"Status",
										].map((header) => (
											<th
												key={header}
												className="px-3 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap"
											>
												{header}
											</th>
										))}
									</tr>
								</thead>
								<tbody className="divide-y divide-slate-100">
									{tableDevices.map((device) => {
										const statusMeta = getStatusMeta(device, device.__rolloutCategory);
										return (
											<tr key={device.device_id} className="hover:bg-slate-50 transition-colors">
												<td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.serial_number ?? "—"}</td>
												<td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.model ?? "—"}</td>
												<td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.current_country ?? "—"}</td>
												<td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.current_fw_version ?? device.current_fw ?? "—"}</td>
												<td className="px-3 py-2 text-slate-600 whitespace-nowrap">{device.base_firmware_version ?? device.base_fw ?? "—"}</td>
												<td className="px-3 py-2 whitespace-nowrap">
													<span className={`inline-block px-2 py-0.5 rounded-full font-semibold capitalize ${statusMeta.className}`}>
														{statusMeta.label}
													</span>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}